// =============================================
// promptVars — walidacja zmiennych {{...}} w edytorze promptów
//
// KNOWN_TEMPLATE_VARS to autorytatywna lista wyciągnięta z TREŚCI wszystkich
// 47 aktywnych promptów w DB (2026-08-23, regexp po prompt_template) — a nie
// z ręcznego glossary, które było nieaktualne (brak zmiennych stepów z 06-07/2026).
//
// Zastosowanie: edytor podświetla zmienną spoza listy jako OSTRZEŻENIE
// (możliwa literówka → `compilePromptSafe` rzuci unresolved-vars w runtime).
// Nigdy nie blokuje zapisu — nowa, celowo dodana zmienna jest legalna,
// o ile serwis ją wypełnia.
// =============================================

export const KNOWN_TEMPLATE_VARS: ReadonlySet<string> = new Set([
  // Automatyczne (doklejane przez withDateVars w kazdym wypelniaczu — fix zlego roku):
  "current_year", "current_date",
  // Link Weaver (etap 5.5 CE; article_html juz na liscie ponizej):
  "link_targets_formatted",
  "areas_list", "article_excerpt", "article_html", "article_lead", "article_text",
  "asl_max", "asl_min", "audience", "author_audience", "author_bio_html",
  "author_identity", "author_label", "author_mission", "author_name", "author_url",
  "best_market_keyword", "boundaries_not_owned", "boundaries_owned",
  "canonical_question", "claim_strength", "claims", "cluster_member_entities",
  "cluster_member_slugs", "content_excerpt", "context", "current_h2_title",
  "data_output", "date_published", "description", "document_role", "domain",
  "domain_context", "domain_name", "eav_context", "eav_formatted", "entities_json",
  "entities_list", "evidence_style", "existing_nodes_in_map", "extracted_attribute",
  "extracted_entity", "extracted_query_frame", "fan_out_result",
  "first_paragraph_answer", "forced_primary_intent", "h1_title", "h2_count",
  "h2_header", "h2_headers", "h2_outline", "h3_count", "h3s", "h3s_formatted",
  "internal_links", "internal_links_formatted", "key_points", "keyword", "kg_data",
  "kg_data_formatted", "kg_distribution_top_entities", "kg_entities",
  "kg_entities_summary", "kg_facts", "kg_ideations", "kg_measurables",
  "kg_relations", "kind", "language", "language_name", "LENGTH_BLOCK",
  "macro_statement", "map_entities_sample", "map_macro_statement", "map_name",
  "map_total_nodes", "map_total_seeds", "market_keywords", "max_strong",
  "meta_description", "min_strong", "name", "ner_output", "node_a_entity",
  "node_a_market_kw", "node_a_question", "node_a_role", "node_a_title",
  "node_b_entity", "node_b_market_kw", "node_b_question", "node_b_role",
  "node_b_title", "node_title", "nodes_chunk", "nodes_list", "number_safety_note",
  "other_seed_names", "outline_headers", "paa_data", "paa_list", "pairs_json",
  "passage_guidance_per_sro_tag", "passage_instructions", "PE", "primary_areas",
  "primary_entity", "primary_intent", "public_author_label", "query",
  "raw_entities", "related_nodes", "relations_sample", "required_entities",
  "role_hint", "sample_eav", "sample_entities", "sample_nodes", "samples",
  "samples_count", "scope_coverage", "scope_entities", "scope_in", "scope_out",
  "secondary_areas", "section_type", "section_type_description", "seed_category",
  "seed_cluster_type", "seed_hint", "seed_intent_family", "seed_intents",
  "seed_name", "seed_names", "seed_names_list", "seed_priority", "seed_type",
  "seeds_list", "sentence_hard_cap", "seo_content_decay", "seo_lost_keywords_count",
  "seo_organic_traffic", "seo_title", "seo_top_10_count", "seo_top_3_count",
  "seo_top_50_count", "serp_content", "service_name", "site_description",
  "site_url", "SOURCE_BLOCK", "source_citation_note", "strong_words",
  "supporting_entities", "time_window", "title", "top_entities",
  "top_entity_names", "top_seed_names", "topic", "user_notes",
  "validated_question", "voice_characteristics", "voice_style",
  "website_category", "website_name", "website_url", "word_count",
  "word_count_target", "wp_content_first_1500", "wp_first_100_words", "wp_title",
  "writing_profile_claim_strength", "writing_profile_core_entities",
  "writing_profile_evidence_style", "writing_profile_h2_patterns",
  "writing_profile_language_rules", "writing_profile_required_sections",
  "writing_profile_use_tables",
]);

const VAR_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Unikalne nazwy zmiennych {{...}} z szablonu, w kolejności pierwszego wystąpienia. */
export function extractTemplateVars(template: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of (template || "").matchAll(VAR_RE)) {
    const v = m[1];
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/** Zmienne z szablonu nieobecne na liście znanych — kandydaci na literówki. */
export function findUnknownVars(template: string): string[] {
  return extractTemplateVars(template).filter((v) => !KNOWN_TEMPLATE_VARS.has(v));
}
